import React, { useState } from 'react';
import { Shield, Search, CheckCircle, Clock, AlertTriangle, Users, Globe, Mail, Phone } from 'lucide-react';
import { API } from '../lib/api';

const EWarranty = () => {
  const [warrantySearch, setWarrantySearch] = useState('');
  const [searchResult, setSearchResult] = useState(null);
  const [isSearching, setIsSearching] = useState(false);

  const runSearch = React.useCallback(async (rawId) => {
    const wid = (rawId || '').trim();
    if (!wid) return;
    setIsSearching(true);
    setSearchResult(null);
    try {
      const response = await fetch(`${API}/warranty/search/${wid}`);
      if (response.ok) {
        const result = await response.json();
        setSearchResult({
          warrantyId: result.warranty_id,
          customerName: result.customer_name,
          vehicleDetails: `${result.vehicle_year} ${result.vehicle_make} ${result.vehicle_model}`,
          serviceType: result.service_type,
          installationDate: result.installation_date,
          expiryDate: result.warranty_expiry,
          lifetime: !!result.lifetime_warranty,
          status: result.status === 'approved' ? 'Active' : result.status.replace('_', ' ').toUpperCase(),
          beforeImages: result.before_images || [],
          afterImages: result.after_images || [],
        });
      } else if (response.status === 404) {
        setSearchResult({ error: true, message: 'Warranty ID not found. Please double-check and try again.' });
      } else {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to search warranty');
      }
    } catch (error) {
      setSearchResult({ error: true, message: error.message || 'Failed to search warranty. Please try again.' });
    } finally {
      setIsSearching(false);
    }
  }, []);

  const handleWarrantySearch = (e) => {
    e.preventDefault();
    runSearch(warrantySearch);
  };

  // Deep-link support: /e-warranty?id=WAR-XXXX auto-runs the lookup.
  React.useEffect(() => {
    try {
      const id = new URLSearchParams(window.location.search).get('id');
      if (id) {
        setWarrantySearch(id);
        runSearch(id);
      }
    } catch { /* non-fatal */ }
  }, [runSearch]);

  const features = [
    { icon: Shield, title: 'Digital Protection', desc: 'Paperless warranty with secure digital certificates and instant verification.' },
    { icon: Globe, title: 'Nationwide Coverage', desc: 'Valid across all Unisol Global locations in India.' },
    { icon: Clock, title: 'Long-term Assurance', desc: 'Coverage up to 10 years for premium PPF and ceramic coatings.' },
    { icon: Users, title: 'Expert Support', desc: '24/7 customer support and expert technical assistance for all claims.' },
  ];

  const benefits = [
    'Material defect coverage',
    'Professional workmanship guarantee',
    'Yellowing and discoloration protection',
    'Cracking and peeling coverage',
    'Free maintenance guidance',
    'Transferable warranty (on vehicle sale)',
    'Quick claim processing',
    'Authorized repair network',
  ];

  return (
    <div className="min-h-screen pt-24" data-testid="ewarranty-page">
      {/* Hero */}
      <section className="py-16 sm:py-20 bg-gradient-to-b from-slate-50 to-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center space-y-6">
          <div className="flex items-center justify-center gap-3">
            <Shield className="w-14 h-14 text-teal-600" />
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900">
              e<span className="text-teal-600">Warranty</span>
            </h1>
          </div>
          <p className="text-lg md:text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            Digital warranty protection for every Unisol Global service.
            Enter your Warranty ID below to instantly verify your coverage.
          </p>
        </div>
      </section>

      {/* Search */}
      <section className="py-12 sm:py-16 bg-white" id="search">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center space-y-3 mb-8">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900">
              Check Your <span className="text-teal-600">Warranty Status</span>
            </h2>
            <p className="text-base text-gray-600">
              Enter the Warranty ID we shared with you to view coverage details.
            </p>
          </div>

          <div className="bg-slate-50 rounded-2xl p-6 sm:p-8 border border-gray-200">
            <form onSubmit={handleWarrantySearch} className="space-y-4" data-testid="warranty-search-form">
              <label className="block text-sm font-semibold text-gray-900">
                Warranty ID
              </label>
              <div className="flex flex-col sm:flex-row gap-3">
                <input
                  type="text"
                  value={warrantySearch}
                  onChange={(e) => setWarrantySearch(e.target.value)}
                  placeholder="e.g. UG12345678"
                  className="flex-1 px-4 py-3 bg-white border border-gray-300 rounded-lg focus:border-teal-500 focus:outline-none transition-colors uppercase font-mono"
                  data-testid="warranty-search-input"
                />
                <button
                  type="submit"
                  disabled={isSearching || !warrantySearch.trim()}
                  className="bg-teal-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-teal-700 transition disabled:opacity-50 flex items-center gap-2 justify-center"
                  data-testid="warranty-search-submit"
                >
                  {isSearching ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      <span>Searching…</span>
                    </>
                  ) : (
                    <><Search size={18} /><span>Search</span></>
                  )}
                </button>
              </div>
            </form>

            {searchResult && (
              <div className="mt-6 bg-white rounded-xl p-5 border border-gray-200" data-testid="warranty-search-result">
                {searchResult.error ? (
                  <div className="flex items-center gap-3">
                    <AlertTriangle className="w-7 h-7 text-red-600 shrink-0" />
                    <div>
                      <h3 className="text-lg font-bold text-gray-900">Warranty Not Found</h3>
                      <p className="text-red-600 text-sm mt-0.5">{searchResult.message}</p>
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="flex items-center gap-3 mb-4">
                      <CheckCircle className="w-7 h-7 text-green-600 shrink-0" />
                      <h3 className="text-lg font-bold text-gray-900">Warranty Found</h3>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                      <Detail label="Warranty ID" value={searchResult.warrantyId} mono />
                      <Detail label="Status" value={searchResult.status} pill />
                      <Detail label="Customer" value={searchResult.customerName} />
                      <Detail label="Vehicle" value={searchResult.vehicleDetails} />
                      <Detail label="Service" value={searchResult.serviceType} />
                      <Detail label="Installed" value={new Date(searchResult.installationDate).toLocaleDateString()} />
                      <Detail
                        label="Valid Until"
                        value={searchResult.lifetime
                          ? 'Lifetime'
                          : (searchResult.expiryDate ? new Date(searchResult.expiryDate).toLocaleDateString() : '—')}
                      />
                    </div>
                    <PhotoGallery
                      title="Before installation"
                      images={searchResult.beforeImages}
                    />
                    <PhotoGallery
                      title="After installation"
                      images={searchResult.afterImages}
                    />
                  </>
                )}
              </div>
            )}

            <p className="text-xs text-gray-500 mt-4 text-center">
              Don&apos;t have a Warranty ID? Contact us at{' '}
              <a href="mailto:warranty@unisolGlobal.com" className="text-teal-600 hover:underline inline-flex items-center gap-1"><Mail size={11} /> warranty@unisolGlobal.com</a>
              {' '}or{' '}
              <a href="tel:+919916120561" className="text-teal-600 hover:underline inline-flex items-center gap-1"><Phone size={11} /> +91 9916120561</a>.
            </p>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-14 sm:py-16 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900">
              Why Choose Our <span className="text-teal-600">eWarranty</span>
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature) => {
              const Icon = feature.icon;
              return (
                <div
                  key={feature.title}
                  className="bg-white rounded-xl p-6 border border-gray-200 hover:border-teal-300 transition-all duration-300 hover:-translate-y-1 text-center"
                >
                  <div className="w-14 h-14 bg-teal-600 rounded-full mx-auto mb-3 flex items-center justify-center">
                    <Icon className="text-white" size={24} />
                  </div>
                  <h3 className="text-base font-bold text-gray-900 mb-2">{feature.title}</h3>
                  <p className="text-sm text-gray-600 leading-relaxed">{feature.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-14 sm:py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-start">
            <div>
              <h2 className="text-3xl sm:text-4xl font-bold text-gray-900">
                Comprehensive <span className="text-teal-600">Coverage</span>
              </h2>
              <p className="text-base text-gray-600 mt-3">
                Our eWarranty covers the things that matter — material, workmanship, and long-term durability.
              </p>
              <div className="bg-teal-50 rounded-lg p-5 border border-teal-200 mt-6">
                <h4 className="font-bold text-teal-800 mb-2 text-sm">Warranty Periods</h4>
                <div className="space-y-1 text-sm text-teal-700">
                  <p><strong>Paint Protection Film:</strong> up to 10 years</p>
                  <p><strong>Ceramic / Graphene Coating:</strong> 5–7 years</p>
                  <p><strong>Window Films:</strong> 5 years</p>
                  <p><strong>Interior Protection:</strong> 3–5 years</p>
                </div>
              </div>
            </div>
            <ul className="space-y-2">
              {benefits.map((b) => (
                <li key={b} className="flex items-center gap-2 text-gray-700 text-sm">
                  <CheckCircle className="text-teal-600 shrink-0" size={18} />
                  <span>{b}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>
    </div>
  );
};

const PhotoGallery = ({ title, images }) => {
  if (!images || images.length === 0) return null;
  // Resolve relative URLs (local-storage media) against the API origin so
  // they render correctly on the public page.
  const apiOrigin = API.replace(/\/api\/?$/, '');
  const resolve = (u) => (u.startsWith('http') ? u : `${apiOrigin}${u}`);
  return (
    <div className="mt-5" data-testid={`warranty-gallery-${title.toLowerCase().replace(/\s+/g, '-')}`}>
      <h4 className="text-xs uppercase tracking-wider text-gray-500 mb-2">{title} ({images.length})</h4>
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
        {images.slice(0, 12).map((url) => (
          <a key={url} href={resolve(url)} target="_blank" rel="noopener noreferrer" className="block">
            <img
              src={resolve(url)}
              alt={title}
              className="w-full h-24 sm:h-28 object-cover rounded border border-gray-200 hover:border-teal-400 transition"
              loading="lazy"
            />
          </a>
        ))}
      </div>
      {images.length > 12 && (
        <p className="text-xs text-gray-500 mt-2">Showing first 12 of {images.length} photos.</p>
      )}
    </div>
  );
};

const Detail = ({ label, value, mono = false, pill = false }) => (
  <div>
    <div className="text-xs uppercase tracking-wider text-gray-500">{label}</div>
    {pill ? (
      <span className="inline-flex mt-1 items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
        <CheckCircle className="w-3 h-3 mr-1" />
        {value}
      </span>
    ) : (
      <p className={`text-gray-800 mt-0.5 ${mono ? 'font-mono text-teal-700 text-base' : ''}`}>{value}</p>
    )}
  </div>
);

export default EWarranty;
